/**
 * Phase 28 — a verdict IN the Flow Optimizer's own rule card (user,
 * 12/10/2026: "make that verdict very professional, easy to understand,
 * minimalist"; same concept, same component): the lead ("The list is
 * terrible"), the FO sentence ("when ALL (AND) / ANY (OR) of these match"),
 * the FO conditions list over the metrics (field, coloured operators, value
 * in the operator's unit, + Condition / + NOT), a footer saying what it is
 * judged on, and the "↳ then" clause with the verdict's one fixed outcome
 * and what it does. Phase 39: the card's SOURCE picks the numbers — today's
 * drops (the gate metrics, DROP tags) or the list's / the gateway's own
 * stats over a date range (the Flow metrics, that item's tags) — and the
 * picker follows. A rule set through the API with nested groups is shown as
 * such and kept exactly as saved. The "metric = meaning" line under the
 * conditions is gone (23/10/2026, client QA) — each condition's hover says it.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { DROP_GEN_RULE_LIMITS, DROPS_SOURCE, type DropGenVerdictCard, type DropGenVerdictSource } from '@trafficpulse/shared';

import { RuleConditionsList, RuleConsequenceFrame, RuleSentence } from '@/features/flow-optimizer/tabs/exclude/group';
import { FormulaMetricCatalogProvider } from '@/features/flow-optimizer/tabs/calculation/metric-catalog/FormulaMetricCatalogContext';
import { metricLabel } from '@/features/flow-optimizer/tabs/exclude/rule-condition/metric-options';

import type { DraftFlatRule, DraftVerdictRow } from '../../../../drop-generator.types';
import { isTagRow } from '../../../../drop-generator-rule.types';
import { tagOptions } from '../../rules/tags/tag-picker';
import { useRuleTagCatalog } from '../../rules/tags/useRuleTagCatalog';
import { SourceChoice } from '../source/SourceChoice';
import { meaningFor, metricCatalogFor, TAG_GROUP, TAG_NOUN, tagSubject, tagSurfaceOf } from '../source/source-words';
import { flatToLines, linesToFlat, newVerdictLine, verdictConditionWords } from './verdict-lines';
import { outcomeEdge, VerdictOutcome, type VerdictOutcomeProps } from './VerdictOutcome';

export interface VerdictCardProps {
  icon: LucideIcon;
  /** "The list is terrible" — the words before the FO sentence's "when". */
  lead: ReactNode;
  rule: DraftFlatRule;
  onPatch: (patch: Partial<Pick<DraftFlatRule, 'join' | 'rows'>>) => void;
  /** The condition "+ Condition" starts from. */
  starter: DraftVerdictRow;
  /** What an empty rule means, when the list's own line is not enough. */
  emptyHint?: string;
  /** The window line under the conditions — what the rule is judged on. */
  footer?: ReactNode;
  outcome: VerdictOutcomeProps;
  /** What the outcome does to the day, under the outcome chip. */
  children?: ReactNode;
  /** Beside the lead — a switch, a count, a note. */
  aside?: ReactNode;
  /** Phase 39 — which card this is (the stop-loss has no source: today's drops). */
  card?: DropGenVerdictCard;
  /** Phase 39 — the numbers judged; absent = today's drops. */
  source?: DropGenVerdictSource;
  /** Phase 39 — present = the card offers the source choice. */
  onSource?: (source: DropGenVerdictSource) => void;
}

export const VerdictCard: FC<VerdictCardProps> = ({ icon: Icon, lead, rule, onPatch, starter, emptyHint, footer, outcome, children, aside, card, source = DROPS_SOURCE, onSource }) => {
  const lines = flatToLines(rule);
  const setLines = (next: typeof lines) => onPatch(linesToFlat(next));
  const add = (not: boolean) =>
    lines.items.length < DROP_GEN_RULE_LIMITS.conditions &&
    setLines({
      ...lines,
      items: [...lines.items, newVerdictLine(starter, not, lines.items.length)],
    });
  const catalog = metricCatalogFor(source.kind);
  const labelOf = (id: string) => metricLabel(catalog, id);
  const meaningOf = (id: string) => meaningFor(source.kind, id);
  const surface = tagSurfaceOf(source);
  const { data: tags } = useRuleTagCatalog(surface);
  const noun = TAG_NOUN[surface];
  const subject = tagSubject(source, card ?? 'stopLoss');
  const missing = tags
    ? rule.rows
        .filter(isTagRow)
        .map((r) => r.tag)
        .filter((t) => !tags.some((c) => c.name === t))
    : [];
  return (
    <article className="space-y-3 rounded-lg border border-border bg-card p-3.5 shadow-sm" data-testid="verdict-card">
      <div className="-mx-3.5 -mt-3.5 flex items-center gap-2 border-b border-border/70 bg-muted/20 px-3.5 py-2 text-[13px] font-semibold">
        <Icon className="h-4 w-4 text-primary" aria-hidden />
        <span>{lead}</span>
        {aside ? <span className="ml-auto flex items-center gap-2 font-normal">{aside}</span> : null}
      </div>
      {card && onSource ? <SourceChoice card={card} source={source} onChange={onSource} /> : null}
      {rule.advanced ? (
        <p className="text-xs italic text-muted-foreground">This rule was set through the API with nested groups — it applies as it is and Save leaves it untouched.</p>
      ) : (
        <FormulaMetricCatalogProvider metrics={catalog}>
          <RuleSentence op={lines.op} onOpChange={(op) => setLines({ ...lines, op })} />
          <RuleConditionsList
            items={lines.items}
            op={lines.op}
            layer="paths"
            tags={tagOptions(tags, TAG_GROUP[surface])}
            rowOptions={{
              rankingMetrics: [],
              allowCompare: false,
              describe: (node) =>
                verdictConditionWords(node, labelOf, {
                  source,
                  subject,
                  meaningOf,
                }),
            }}
            onReplace={(next) =>
              setLines({
                ...lines,
                items: lines.items.map((i) => (i.id === next.id ? next : i)),
              })
            }
            onRemove={(id) =>
              setLines({
                ...lines,
                items: lines.items.filter((i) => i.id !== id),
              })
            }
            onAddCondition={() => add(false)}
            onAddNot={() => add(true)}
            footer={footer}
          />
          {rule.rows.length === 0 && emptyHint ? <p className="text-xs text-muted-foreground">{emptyHint}</p> : null}
          {missing.length > 0 ? (
            <p className="text-[11px] leading-snug text-warning" data-testid="verdict-missing-tags">
              No enabled {noun} tag is named {missing.map((t) => `"${t}"`).join(', ')} — that condition stays unknown, and unknown never acts.
            </p>
          ) : null}
        </FormulaMetricCatalogProvider>
      )}
      <RuleConsequenceFrame edgeClass={outcomeEdge(outcome.tone)} picker={<VerdictOutcome {...outcome} />}>
        {children}
      </RuleConsequenceFrame>
    </article>
  );
};
